/**
 * Router-level Wake Lock manager.
 * Prevents screen sleep during active gameplay (night quests).
 * Managed at router level, not per-view, to avoid unlock gaps on transitions.
 */

let sentinel: WakeLockSentinel | null = null;
let active = false;

async function tryAcquire(): Promise<void> {
  if (!('wakeLock' in navigator)) return;
  if (sentinel !== null) return; // already held

  try {
    sentinel = await navigator.wakeLock.request('screen');
    sentinel.addEventListener('release', () => {
      sentinel = null;
      // Don't set active=false here — visibilitychange will reacquire if needed
    });
  } catch {
    // Permission denied or not available — silently degrade
    sentinel = null;
  }
}

export function acquireWakeLock(): void {
  active = true;
  tryAcquire();
}

export function releaseWakeLock(): void {
  active = false;
  if (sentinel) {
    sentinel.release().catch(() => {});
    sentinel = null;
  }
}

export function isWakeLockActive(): boolean {
  return active && sentinel !== null;
}

// Reacquire on tab foreground — Wake Lock auto-releases on background.
// Uses retry with delay for iOS race condition.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && active) {
    // Delay reacquire to avoid iOS race condition
    setTimeout(() => {
      if (active && sentinel === null) {
        tryAcquire();
      }
    }, 300);
  }
});
